import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { inviteMembers } from 'services/membersService';

import { isValidEmail } from 'utlis/globalFunctions';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';

import styles from './InviteModal.module.scss';

const InviteModal = ({ isOpen, onClose, onInviteSuccess, workspaceUuid }) => {
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const inputRef = useRef(null);

  const hasInvalidEmail = useMemo(() => emails.some(e => !isValidEmail(e)), [emails]);

  const addEmail = value => {
    const trimmed = value.trim().replace(/,$/, '')
      .trim();

    if (!trimmed || emails.includes(trimmed)) {
      setInputValue('');

      return;
    }

    setEmails(prev => [...prev, trimmed]);
    setInputValue('');
  };

  const removeEmail = email => {
    setEmails(prev => prev.filter(e => e !== email));
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail(inputValue);
    }
    else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      setEmails(prev => prev.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const parts = pasted.split(/[\s,;]+/).filter(Boolean);

    if (parts.length > 1) {
      const unique = parts.filter(p => !emails.includes(p));

      setEmails(prev => [...prev, ...unique]);
    }
    else {
      setInputValue(prev => prev + parts[0]);
    }
  };

  const handleClose = () => {
    setEmails([]);
    setInputValue('');
    onClose();
  };

  const handleInvite = async () => {
    if (emails.length === 0 || isInviting) {
      return;
    }

    setIsInviting(true);

    try {
      const { created = [], resent = [], skipped = [] } = await inviteMembers({ workspaceUuid, emails });

      const parts = [];

      if (created.length > 0) {
        parts.push(`Invite sent to ${created.length} ${created.length === 1 ? 'member' : 'members'}`);
      }

      if (resent.length > 0) {
        parts.push(`Invite resent to ${resent.length} ${resent.length === 1 ? 'member' : 'members'}`);
      }

      const message = parts.length > 0
        ? parts.join(', ')
        : 'No new invitations sent';

      const skippedSuffix = skipped.length > 0
        ? `. ${skipped.length} ${skipped.length === 1 ? 'email' : 'emails'} skipped (already a member)`
        : '';

      if (parts.length > 0) {
        successReactToastify(`${message}${skippedSuffix}.`);
      }
      else {
        errorReactToastify(`No new invitations sent${skippedSuffix}.`);
      }

      onInviteSuccess?.();
      handleClose();
    }
    catch (err) {
      errorReactToastify(err?.message || 'Failed to send invitations');
    }
    finally {
      setIsInviting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles['overlay']} onClick={handleClose}>
      <div className={styles['card']} onClick={e => e.stopPropagation()}>
        <div className={styles['header']}>
          <h2 className={styles['title']}>
            Invite members
          </h2>
          <button className={styles['close-btn']} onClick={handleClose} type="button">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className={styles['body']}>
          <div className={styles['input-row']}>
            {/* Email chip input */}
            <div
              className={styles['chip-input']}
              onClick={() => inputRef.current?.focus()}
            >
              <div>
                {emails.map(email => (
                  <span
                    key={email}
                    className={`${styles['chip']} ${!isValidEmail(email) ? styles['chip-error'] : ''}`}
                  >
                    {email}
                    <button
                      type="button"
                      className={styles['chip-remove']}
                      onClick={e => {
                        e.stopPropagation();
                        removeEmail(email);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  ref={inputRef}
                  type="email"
                  name="invite-email"
                  autoComplete="email"
                  className={styles['chip-text-input']}
                  placeholder={emails.length === 0 ? 'Emails, separated by commas' : ''}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  onPaste={handlePaste}
                />
              </div>
              <span className={styles['can-view-label']}>
                Can view
              </span>
            </div>

            {/* Permission badge */}
            <button
              type="button"
              className={styles['permission-badge']}
              disabled={emails.length === 0 || isInviting || hasInvalidEmail}
              onClick={handleInvite}
            >
              Invite
            </button>
          </div>
          <span className={styles['field-help']}>
            Can access the audits, but can&apos;t create a new one
          </span>
        </div>
      </div>
    </div>
  );
};

InviteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInviteSuccess: PropTypes.func,
  workspaceUuid: PropTypes.string,
};

export default InviteModal;
